// src/components/AiChatBox.jsx
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { askAI, AI_PROMPT_MAX_CHARS } from "../api/aiClient";

/**
 * AI chat dialog - sends a prompt (plus optional context) to the TMS backend.
 * Props:
 *   open    - boolean, controls visibility
 *   onClose - callback to close the dialog
 *   context - optional pre-filled context (e.g., driver name, task details)
 */
export default function AiChatBox({ open, onClose, context = "" }) {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState("");

  const handleSend = async () => {
    if (!prompt.trim() || loading) return;
    setLoading(true);
    setResponse("");
    try {
      const text = await askAI(prompt.trim(), context);
      setResponse(text || "The assistant returned an empty reply.");
    } catch (err) {
      setResponse(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>AI Assistant</DialogTitle>
      <DialogContent dividers>
        {context && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Context: {context}
          </Typography>
        )}
        <Box mb={2} sx={{ maxHeight: 300, overflowY: "auto" }}>
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <Typography variant="body2" component="pre" sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit", m: 0 }}>
              {response || "Ask a question."}
            </Typography>
          )}
        </Box>
        <TextField
          fullWidth
          placeholder="Type your question."
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          disabled={loading}
          inputProps={{ maxLength: AI_PROMPT_MAX_CHARS }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Close
        </Button>
        <Button onClick={handleSend} variant="contained" disabled={loading || !prompt.trim()}>
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
}
